<template>
  <div class="login-page-container">
    <div class="login-page-header">
      <a href="" class="login-page-logo" @click="goToHome">
        <img src="@/assets/images/logo-white.svg" alt="Logo" />
      </a>
      <span class="login-page-title">{{ $t('login.header_title') }}</span>
    </div>
    <div class="login-page-content">
      <a-form ref="form" :model="form" :wrapper-col="{ span: 24 }">
        <div class="form-logo">
          <img src="@/assets/images/logo.svg" alt="Logo" />
        </div>
        <a-form-item
          name="username"
          :rules="{
            type: 'string',
            required: true,
            message: $t('login.form_username_placeholder')
          }"
        >
          <a-input
            v-model:value="form.username"
            :placeholder="$t('login.form_username_placeholder')"
            :maxLength="30"
          >
            <template #prefix><UserIcon type="user"/></template>
          </a-input>
        </a-form-item>
        <a-form-item
          name="password"
          :rules="{
            type: 'string',
            required: true,
            message: $t('login.form_password_placeholder')
          }"
        >
          <a-input-password
            :visibilityToggle="false"
            v-model:value="form.password"
            :placeholder="$t('login.form_password_placeholder')"
            :maxLength="30"
          >
            <template #prefix><LockIcon type="user"/></template>
          </a-input-password>
        </a-form-item>
        <a-form-item
          v-if="false"
          name="captcha"
          :rules="[
            {
              type: 'string',
              required: true,
              message: $t('login.form_captcha_placeholder')
            },
            { captcha }
          ]"
        >
          <a-input
            v-model:value="form.captcha"
            :placeholder="$t('login.form_captcha_placeholder')"
          />
          <a-button @click="getValidateCode">
            <span v-if="!validateImg">{{ $t('login.form_captcha_get') }}</span>
            <img
              v-if="validateImg"
              :src="validateImg"
              :alt="$t('login.form_captcha_title')"
            />
          </a-button>
        </a-form-item>
        <a-form-item>
          <a-button block type="primary" @click="onSubmit" :loading="loading">
            {{ $t('login.form_login') }}
          </a-button>
        </a-form-item>
      </a-form>
    </div>
    <div class="login-page-footer">
      <span
        >Copyright © 2010-{{ currentYear }}, www.cnjauto.com.cn, All rights
        reserved</span
      >
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapState } from 'vuex';
import { getValidateCode, loginUser } from '@/api/apis';
import router from '../router';
import { encrypt } from '@/utils/crypt';
import UserIcon from '@/components/icons/UserIcon.vue';
import LockIcon from '@/components/icons/LockIcon.vue';

export default defineComponent({
  name: 'Login',
  components: {
    UserIcon,
    LockIcon
  },
  data() {
    return {
      loading: false,
      validateImg: null,
      form: {
        username: undefined,
        password: undefined,
        checkKey: 'key_' + (Math.floor(Math.random() * 10000) + 10000),
        captcha: undefined
      }
    };
  },
  created() {
    this.getEncryptedString();
  },
  computed: {
    ...mapState({
      encryptedString: (state: any) => state.encryptedString
    }),
    currentYear() {
      return new Date().getFullYear();
    }
  },
  methods: {
    ...mapActions(['getEncryptedString', 'setTokenAndUserInfo']),
    onSubmit() {
      (this.$refs as any).form
        .validate()
        .then(async () => {
          this.loading = true;
          const res = await loginUser({
            ...this.form,
            password: encrypt(
              this.form.password,
              this.encryptedString.key,
              this.encryptedString.iv
            )
          });
          if (res.success) {
            this.setTokenAndUserInfo({ ...res.result });

            if (router.currentRoute.value.redirectedFrom) {
              const redirectPath =
                router.currentRoute.value.redirectedFrom?.path;
              const query = router.currentRoute.value.redirectedFrom?.query;
              router.replace({
                path: redirectPath,
                query
              });
            } else {
              if (router.currentRoute.value.query.redirect) {
                router.replace({
                  path: router.currentRoute.value.query.redirect as string
                });
              } else {
                router.replace({
                  path: '/vehicle'
                });
              }
            }
          }
          this.loading = false;
        })
        .catch(error => error);
    },
    goToHome() {
      this.$router.push('/');
    },
    async getValidateCode() {
      const res = await getValidateCode(this.form.checkKey);
      if (res.success) {
        this.validateImg = res.result;
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.login-page-container {
  min-width: 1200px;
  height: 100%;
  min-height: max(100%, 720px);
  background-image: url('~@/assets/images/BG.jpg');
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex-direction: column;
}

.login-page-header {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 17px 40px;
  display: flex;
  align-items: center;

  .login-page-logo {
    img {
      height: 46px;
    }
  }

  .login-page-title {
    font-family: Helvetica;
    font-size: 34px;
    color: #ffffff;
    letter-spacing: 0;
    line-height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &::before {
      content: '';
      display: inline-block;
      width: 1px;
      height: 40px;
      border-right: 1px solid #fff;
      margin-left: 40px;
      margin-right: 40px;
    }
  }
}

.login-page-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-form {
    width: 500px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 10px;
    padding: 70px 75px 60px;
    animation: bounceInDown 700ms;

    @keyframes bounceInDown {
      0% {
        opacity: 0;
        transform: translateY(-200px);
      }

      60% {
        opacity: 1;
        transform: translateY(30px);
      }

      80% {
        transform: translateY(-10px);
      }

      100% {
        transform: translateY(0);
      }
    }

    .form-logo {
      margin-bottom: 50px;
      text-align: center;

      img {
        height: 60px;
      }
    }

    .ant-form-item {
      margin-bottom: 0;
      & + .ant-form-item {
        margin-top: 25px;
      }
      &:last-child {
        margin-top: 40px;
      }
    }

    ::v-deep .ant-form-item-children {
      display: flex;
    }

    .ant-input-affix-wrapper,
    .ant-input-password,
    .ant-input {
      background: #ffffff;
      border: 1px solid #d9d9d9;
      padding: 13px 20px;

      & + .ant-btn {
        margin-left: 10px;
        height: 50px;
        padding: 5px;
        min-width: 117px;
        font-size: 14px;
      }
    }

    ::v-deep .ant-input-prefix {
      margin-right: 20px;
    }

    .ant-btn {
      font-weight: bold;
      font-size: 20px;
      letter-spacing: 0;
      height: 58px;
    }
  }
}

.login-page-footer {
  margin-bottom: 50px;
  font-size: 12px;
  color: #ffffff;
  letter-spacing: 0.09px;
  text-align: center;
}

::v-deep input::-webkit-input-placeholder {
  font-size: 14px;
  color: #cecece;
  letter-spacing: 0.1px;
}

::v-deep input:-webkit-autofill,
::v-deep input:-webkit-autofill:hover,
::v-deep input:-webkit-autofill:focus,
::v-deep input:-webkit-autofill:active {
  -webkit-transition-delay: 99999s;
  -webkit-transition: color 99999s ease-out, background-color 99999s ease-out;
}
</style>
